'use strict';

import { timeFormat } from 'd3-time-format'
import { select, selectAll} from 'd3-selection'
import { scaleLinear, scaleTime } from 'd3-scale'
import { axisBottom, axisLeft, axisRight } from 'd3-axis'
import { max, min } from 'd3-array'
import { line, area } from 'd3-shape'
import React from 'react'
import ReactDOM from 'react-dom'
import activateConfirmationBox from './confirmation-box.jsx'
import { addToArrayIfNotExists, removeFromArrayIfExists } from '../common/helpers.js'
import { handleLoggedOutError } from '../common/ajax-error-handlers.jsx'
import { ajaxRequest } from '../common/ajax.js'

function meanWeightedByLapDuration(laps, property_name) {

  // Property name can also be a function.

  if (laps.length == 0) {
    return null;
  }

  var total_duration_seconds = 0;
  var sum_of_weighted_values = 0;
  for (var i = 0; i < laps.length; i++) {
    var lap = laps[i];

    if (typeof(lap[property_name]) == 'function') {
      var value = lap[property_name]();
    }
    else {
      var value = lap[property_name];
    }

    if (value) {
      total_duration_seconds += lap.duration_seconds;
      sum_of_weighted_values += value * lap.duration_seconds;
    }
  }

  if (sum_of_weighted_values > 0) {
    return sum_of_weighted_values / total_duration_seconds;
  }
  else {
    return null;
  }

}

function getTotalDistanceKilometers(laps) {
  return xhale.helper.meters_to_km(sumProperty(laps, 'distance_meters'));
}

function getTotalDistanceMiles(laps) {
  return xhale.helper.meters_to_miles(sumProperty(laps, 'distance_meters'));
}

export var getAveragePower = function (laps) {
  return meanWeightedByLapDuration(laps, 'average_power_watts');
}

export function getAverageHeartRate(laps) {
  return meanWeightedByLapDuration(laps, 'average_heart_rate_bpm');
}

export function getAverageCadence(laps) {
  return meanWeightedByLapDuration(laps, 'average_cadence_rpm');
}

export function getAverageSpeedMetersPerSecond(laps) {
  return meanWeightedByLapDuration(laps, 'get_speed_meters_per_second');
}

function sumProperty(laps, property_name) {

  var total_value = 0;

  for (var i = 0; i < laps.length; i++) {

    var lap = laps[i];

    if (typeof(lap[property_name]) == 'function') {
      var value = lap[property_name]();
    }
    else {
      var value = lap[property_name];
    }

    if (value) {
      total_value += value;
    }

  }

  return total_value;
}

export class SummaryTable extends React.Component {

  handleSaveSelectedSessions = () => {
    if (this.props.compare_sessions_current_selection.name) {
      this.props.compare_sessions_current_selection.save(this.props.handleSelectedSessionSaved);
    }
    else {
      // Pop up a form to create a session name.
      this.props.openSaveSelectionForm(this.props.compare_sessions_current_selection, this.props.base_activity_id, this.props.handleSelectedSessionSaved);
    }
  }

  render() {

    var self = this;

    var lap_table_headers = [];
    var total_columns = 0;

    // Create the appropriate header columns based on what lap features are supported.
    lap_table_headers.push(<th key="title">Date</th>);
    total_columns++;

    if (this.props.supported_metrics.lap_count) {
      lap_table_headers.push(<th key="lap-count" className="distance">Laps</th>);
      total_columns++;
    }
    if (this.props.supported_metrics.distance) {
      if (this.props.discipline == 'swimming') {
        lap_table_headers.push(<th key="distance" className="distance">Total distance</th>);
        total_columns++;
      }
      else {
        lap_table_headers.push(<th key="distance" className="distance">Distance</th>);
        total_columns++;
      }
    }
    if (this.props.supported_metrics.duration) {
      var header_text = (this.props.discipline == 'swimming') ? 'Lap time' : 'Duration';
      lap_table_headers.push(<th key="duration" className="duration">{header_text}</th>);
      total_columns++;
    }
    if (this.props.supported_metrics.speed) {
      if (this.props.discipline == 'running') {
        lap_table_headers.push(<th key="speed" className="speed">Average pace</th>);
        total_columns++;
      }
      else if (this.props.discipline == 'swimming') {
        lap_table_headers.push(<th key="speed" className="speed">Pace min/100m</th>);
        total_columns++;
      }
      else {
        lap_table_headers.push(<th key="speed" className="speed">Speed</th>);
        total_columns++;
      }
    }
    if (this.props.supported_metrics.height_climbed) {
      lap_table_headers.push(<th key="height-climbed" className="height-climbed">Climbed</th>);
      total_columns++;
    }
    if (this.props.supported_metrics.heart_rate) {
      lap_table_headers.push(<th key="heart-rate" className="heart-rate">Heart</th>);
      total_columns++;
    }
    if (this.props.supported_metrics.average_power) {
      lap_table_headers.push(<th key="power" className="power">Power</th>);
      total_columns++;
    }
    if (this.props.supported_metrics.normalized_power) {
      lap_table_headers.push(<th key="normalized-power" className="power">Normalised</th>);
      total_columns++;
    }
    if (this.props.supported_metrics.cadence) {
      lap_table_headers.push(<th key="cadence" className="cadence">Cadence</th>);
      total_columns++;
    }
    if (typeof(this.props.toggleDisplayActivityOnChart) != 'undefined') {
      lap_table_headers.push(<th key="buttons" className="buttons"></th>);
      total_columns++;
    }

    var lap_table_headers;
    var table_rows = [];
    var lap_set = this.props.lapsets[this.props.current_lapset_id];

    this.props.activity_summaries.sort(function (a, b) { return b.start_time - a.start_time });

    for (var activity_index = 0; activity_index < this.props.activity_summaries.length; activity_index++) {
      var activity = this.props.activity_summaries[activity_index];

      if (activity.activity_id == this.props.base_activity_id) {
        var text_colour = get_discipline_colour(this.props.discipline);
      }
      else if (typeof(this.props.visible_lapset_activity_colours[activity.activity_id]) != 'undefined') {
        var text_colour = this.props.visible_lapset_activity_colours[activity.activity_id];
      }
      else {
        var text_colour = '#000000';
      }

      var currently_displaying_on_chart = (this.props.visible_lapset_activities.indexOf(activity.activity_id) > -1) ? true : false;

      table_rows.push(
        <LapSetSummaryRow
          key={'summary-row-' + activity.activity_id}
          updateActivitySection={this.props.updateActivitySection}
          show_date={activity.start_time}
          activity={activity}
          discipline={this.props.discipline}
          base_activity_id={this.props.base_activity_id}
          supported_metrics={this.props.supported_metrics}
          current_lapset_id={this.props.current_lapset_id}
          text_colour={text_colour}
          currently_displaying_on_chart={currently_displaying_on_chart}
          laps={this.props.laps}
          lap_set={lap_set}
          distance_unit={this.props.distance_unit}
          lap_set_activity_summaries={this.props.lap_set_activity_summaries}
          compare_sessions_current_selection={this.props.compare_sessions_current_selection}
          toggleDisplayActivityOnChart={this.props.toggleDisplayActivityOnChart}
          displayActivityOnChart={this.props.displayActivityOnChart}
          deleteActivityFromSelection={this.props.deleteActivityFromSelection}
          addActivityToSelection={this.props.addActivityToSelection}
        />
      );

    }

    if (this.props.compare_sessions_current_selection.hasUnsavedChanges()) {
      var save_session_selection_button = <div style={{float: 'right', margin: '4px'}} onClick={this.handleSaveSelectedSessions}><span className="small-button">Save this selection</span></div>
    }
    else {
      var save_session_selection_button = <div style={{float: 'right', margin: '4px'}}><span className="small-button small-button_disabled">Save this selection</span></div>
    }

    return (
      <div className={'lap-table'} style={{clear: 'both'}}>
        <table style={{width: '100%'}}>
          <thead>
            <tr>
              {lap_table_headers}
            </tr>
          </thead>
          <tbody>
            {table_rows}
          </tbody>
        </table>
        <div style={{float: 'right', margin: '4px'}} onClick={this.props.handleOpenCollection}><span className="small-button">Compare other sessions</span></div>
        {save_session_selection_button}
      </div>
    );

  }
}

class LapSetSummaryRow extends React.Component {

  toggleDisplayActivityOnChart = () => {
    this.props.toggleDisplayActivityOnChart(this.props.activity.activity_id);
  }
  
  addLapsToActivity = () => {

    this.props.displayActivityOnChart(this.props.activity.activity_id, () => {

      this.props.updateActivitySection({
        chart_display_mode: 'normal',
        active_extras_tab: 'laps',
        lapset_edit_mode: {
          active: true,
          editing_lapset: this.props.current_lapset_id,
          editing_activity: parseInt(this.props.activity.activity_id),
        }
      });
    });

  }

  deleteActivityFromSelection = (event) => {
    this.props.deleteActivityFromSelection(this.props.activity);
  }

  addActivityToSelection = (event) => {
    this.props.addActivityToSelection(this.props.activity);
  }

  getStatusInSelection = () => {

    if (this.props.activity.training_session_id) {
      var in_unsaved_selection = this.props.compare_sessions_current_selection.unsaved_session_ids.indexOf(this.props.activity.training_session_id) > -1;
      var in_saved_selection = this.props.compare_sessions_current_selection.saved_session_ids.indexOf(this.props.activity.training_session_id) > -1;
    }
    else if (this.props.activity.race_id) {
      var in_unsaved_selection = this.props.compare_sessions_current_selection.unsaved_race_ids.indexOf(this.props.activity.race_id) > -1;
      var in_saved_selection = this.props.compare_sessions_current_selection.saved_race_ids.indexOf(this.props.activity.race_id) > -1;
    }

    if (in_unsaved_selection && in_saved_selection) {
      return 'saved';
    }
    else if (in_unsaved_selection && !in_saved_selection) {
      return 'unsaved';
    }
    else {
      return 'removed';
    }

  }

  render() {

    var status_in_selection = this.getStatusInSelection();

    var columns = [];

    // Grab the summary for this activity and lapset, if it exists.
    if (this.props.lap_set_activity_summaries[this.props.activity.activity_id] && this.props.lap_set_activity_summaries[this.props.activity.activity_id][this.props.lap_set.id]) {
      var lapset_activity_summary = this.props.lap_set_activity_summaries[this.props.activity.activity_id][this.props.lap_set.id];
    }
    else {
      var lapset_activity_summary = null;
    }

    if (this.props.show_date) {
      var start_time = this.props.show_date;
      columns.push(<td key='date' className="date"><div className="cell-content">{start_time.format('d M Y')}</div></td>);
    }
    else {
      columns.push(<td key='totals' className="totals"><div className="cell-content">Total</div></td>);
    }

    // Not every activity in the set will necessarily have a lap
    // set created.
    if (!lapset_activity_summary) {
      var column_count = 0;
      if (this.props.supported_metrics.lap_count) {
        column_count++;
      }
      if (this.props.supported_metrics.distance) {
        column_count++;
      }
      if (this.props.supported_metrics.duration) {
        column_count++;
      }
      if (this.props.supported_metrics.speed) {
        column_count++;
      }
      if (this.props.supported_metrics.height_climbed) {
        column_count++;
      }
      if (this.props.supported_metrics.heart_rate) {
        column_count++;
      }
      if (this.props.supported_metrics.average_power) {
        column_count++;
      }
      if (this.props.supported_metrics.normalized_power) {
        column_count++;
      }
      if (this.props.supported_metrics.cadence) {
        column_count++;
      }

      columns.push(<td key='no-laps' style={{textAlign: 'left', fontStyle: 'italic', color: '#a7a7a7'}} colSpan={column_count}>No laps have been saved to this selection</td>)

    }
    else {
      if (this.props.supported_metrics.lap_count) {
        columns.push(<td key='lap-count' className="lap-count"><div className="cell-content">{lapset_activity_summary.lap_count}</div></td>);
      }
      if (this.props.supported_metrics.distance) {
        if (this.props.distance_unit == 'km') {
          columns.push(<td key='distance-km' className="distance"><div className="cell-content">{xhale.helper.format_km(xhale.helper.meters_to_km(lapset_activity_summary.distance_meters), 2)}<span className="unit">km</span></div></td>);
        }
        else {
          columns.push(<td key='distance-miles' className="distance"><div className="cell-content">{xhale.helper.format_miles(xhale.helper.meters_to_miles(lapset_activity_summary.distance_meters), 2)}<span className="unit">miles</span></div></td>);
        }
      }
      if (this.props.supported_metrics.duration) {
        columns.push(<td key='duration' className="duration"><div className="cell-content">{xhale.helper.format_time_seconds(lapset_activity_summary.duration_seconds)}</div></td>);
      }
      if (this.props.supported_metrics.speed) {

        var meters_per_second = lapset_activity_summary.distance_meters / lapset_activity_summary.duration_seconds;

        if (this.props.discipline == 'running') {
          if (this.props.distance_unit == 'km') {
            columns.push(<td key='speed-min-km' className="speed"><div className="cell-content">{xhale.helper.format_time(xhale.helper.meters_per_second_as_seconds_per_km(meters_per_second))}<span className="unit">min/km</span></div></td>);
          }
          else {
            columns.push(<td key='speed-min-mile' className="speed"><div className="cell-content">{xhale.helper.format_time(xhale.helper.meters_per_second_as_seconds_per_mile(meters_per_second))}<span className="unit">min/mi</span></div></td>);
          }
        }
        else {
          if (this.props.distance_unit == 'km') {
            columns.push(<td key='speed-kph' className="speed"><div className="cell-content">{xhale.helper.format_km(xhale.helper.meters_per_second_to_kph(meters_per_second), 2)}<span className="unit">kph</span></div></td>);
          }
          else {
            columns.push(<td key='speed-mph' className="speed"><div className="cell-content">{xhale.helper.format_miles(xhale.helper.meters_per_second_to_mph(meters_per_second), 2)}<span className="unit">mph</span></div></td>);
          }
        }
      }
      if (this.props.supported_metrics.height_climbed) {
        if (this.props.distance_unit == 'km') {
          columns.push(<td key='climb-meters' className="climb-meters"><div className="cell-content">{xhale.helper.decimal_places(lapset_activity_summary.total_climbed_meters)}<span className="unit">m</span></div></td>);
        }
        else {
          columns.push(<td key='climb-feet' className="climb-feet"><div className="cell-content">{xhale.helper.decimal_places(xhale.helper.meters_to_feet(lapset_activity_summary.total_climbed_meters))}<span className="unit">ft</span></div></td>);
        }
      }
      if (this.props.supported_metrics.heart_rate) {
        columns.push(<td key='heart-rate' className="heart-rate"><div className="cell-content">{xhale.helper.decimal_places(lapset_activity_summary.average_heart_rate_bpm, 0)}<span className="unit">bpm</span></div></td>);
      }
      if (this.props.supported_metrics.average_power) {
        // TODO Currently left blank
        if (false || athlete_weight) {
          //var average_watts_per_kg = xhale.helper.decimal_places(this.props.lap.average_power_watts / athlete_weight, 2, 'zero', true);
          //var average_power_to_weight_jsx = (<span className="power-to-weight"> <span className="value">{average_watts_per_kg}</span><span className="unit">w/kg</span></span>);
        }
        else {
          var average_power_to_weight_jsx = '';
        }
        columns.push(<td key='average-power' className="power"><div className="cell-content">{xhale.helper.decimal_places(lapset_activity_summary.average_power_watts, 0)}<span className="unit">watts</span>{average_power_to_weight_jsx}</div></td>);
      }
      if (this.props.supported_metrics.normalized_power) {
        // TODO Currently left blank
        if (athlete_weight) {
          //var normalized_watts_per_kg = xhale.helper.decimal_places(this.props.lap.normalized_power_watts / athlete_weight, 2, 'zero', true);
          //var normalized_power_to_weight_jsx = (<span className="power-to-weight"> <span className="value">{normalized_watts_per_kg}</span><span className="unit">w/kg</span></span>);
        }
        else {
          var normalized_power_to_weight_jsx = '';
        }
        columns.push(<td key='normalized-power' className="power"><div className="cell-content">{xhale.helper.decimal_places(lapset_activity_summary.normalized_power_watts, 0)}<span className="unit">watts</span>{normalized_power_to_weight_jsx}</div></td>);
      }
      if (this.props.supported_metrics.cadence) {
        if (this.props.discipline == 'running') {
          var value = lapset_activity_summary.average_cadence_rpm * 2;
          var unit = 'spm';
        }
        else {
          var value = lapset_activity_summary.average_cadence_rpm;
          var unit = 'rpm';
        }
        columns.push(<td key='cadence' className="cadence"><div className="cell-content">{xhale.helper.decimal_places(value, 0)}<span className="unit">{unit}</span></div></td>);
      }
    }

    var style = {};
    if (status_in_selection == 'removed') {
      style.color = '#C7C7C7';
    }
    else {
      style.color = this.props.text_colour;
    }

    if (!this.props.show_date) {
      style.fontWeight = 'bold';
    }

    if (typeof(this.props.toggleDisplayActivityOnChart) != 'undefined') {

      if (status_in_selection == 'saved' || status_in_selection == 'unsaved') {
       var remove_button = <span className="table-button" onClick={this.deleteActivityFromSelection}>delete from selection</span>;
      }
      else {
       var remove_button = <span className="table-button" onClick={this.addActivityToSelection}>add back to selection</span>;
      }

      if (this.props.activity.activity_id != this.props.base_activity_id) {
        // TODO At the moment the hide or show on chart button does not work for the current activity. Change this.
        if (this.props.currently_displaying_on_chart) {
          var show_on_chart_button = <span className="table-button" onClick={this.toggleDisplayActivityOnChart} style={{width: '78px'}}>hide on chart</span>;
        }
        else {
          var show_on_chart_button = <span className="table-button" onClick={this.toggleDisplayActivityOnChart} style={{width: '78px'}}>show on chart</span>;
        }
      }
      else {
        var show_on_chart_button = <span className="table-button table-button-disabled" style={{width: '78px'}}>hide on chart</span>;
      }

      var edit_laps_button;
      if (this.props.current_lapset_id != 'overall_activity') {
        edit_laps_button = <span className="table-button" target="blank" onClick={this.addLapsToActivity}>edit laps</span>;
      }

      columns.push(
        <td key='menu' className="menu">
          <div className="cell-content">{edit_laps_button} {remove_button} {show_on_chart_button}</div>
        </td>
      );
    }
    else {
      columns.push(<td key='menu' className="menu"></td>);
    }

    var class_name = 'status-' + status_in_selection;

    return (
      <tr style={style} className={class_name}>
        {columns}
      </tr>
    );

  }
}

export class LapRow extends React.Component {

  toggleRow = () => {
    this.props.lap.visible = (this.props.lap.visible) ? false : true; // Reverse visiblity
    this.props.handleLapUpdate(this.props.lap);
  }

  handleHideClicked = (event) => {
    this.toggleRow();
    event.stopPropagation();
  }

  handleCompareClicked = (event) => {
    event.stopPropagation(); // Avoid also highlighting this lap.
    this.props.handleCompareClicked(this.props.lap);
  }

  handleRemoveFromSetClicked = (event) => {
    event.stopPropagation(); // Avoid also highlighting this lap.
    activateConfirmationBox({
      message: "Are you sure you want to remove this lap from the " + this.props.current_lapset.name + " set?",
      confirm_callback: this.confirmRemoveFromSet,
      yes_button_text: "Yes, remove it",
    });
  }

  handleAddToSetClicked = (event) => {

    event.stopPropagation(); // Avoid also highlighting this lap.

    let post_data = {
      id: null,
      existing_set_id: (this.props.current_lapset.id != 'new_lapset') ? this.props.current_lapset.id : null,
      name: this.props.current_lapset.name,
      discipline: this.props.discipline,
      athlete_id: this.props.athlete_id,
      lap_ids_to_add: [this.props.lap.id],
    }

    ajaxRequest({
      url: '/training-logs/save-lap-set/',
      method: 'POST',
      data: post_data,
      success: this.lapAddedToSetCallback,
      expected_response_type: 'json',
    });

  }

  lapAddedToSetCallback = (data, status) => {
    addToArrayIfNotExists(this.props.lap.lapsets, data.lap_set.id);
    this.props.updateLap(this.props.lap, data.lap_set_activity_summary, data.lap_set, data.new_lapset);
  }

  confirmRemoveFromSet = () => {

    var post_data = {
      athlete_id: this.props.athlete_id,
      existing_set_id: this.props.current_lapset.id,
      lap_ids_to_remove: [this.props.lap.id],
      csrfmiddlewaretoken: this.props.csrf_token
    }

    ajaxRequest({
      url: '/training-logs/save-lap-set/',
      method: 'POST',
      data: post_data,
      success: this.lapRemovedFromSetCallback,
      expected_response_type: 'json',
    });
  }

  lapRemovedFromSetCallback = (data, status) => {
    removeFromArrayIfExists(this.props.lap.lapsets, data.lap_set.id);
    this.props.updateLap(this.props.lap, data.lap_set_activity_summary);
  }

  handleRowClicked = (event) => {
    if (!this.props.lap.visible) {
      this.toggleRow();
    }
    else if (this.props.highlightable) {
      this.props.handleRowHighlighted(this.props.lap);
    }
  }

  render() {
    var columns = [];

    if (this.props.lap_number) {
      columns.push(<td key='totals' className="totals"><div className="cell-content">{this.props.lap_number}</div></td>);
    }
    if (this.props.lap_features.distance) {
      if (this.props.distance_unit == 'km') {
        columns.push(<td key='distance-km' className="distance"><div className="cell-content">{xhale.helper.format_km(this.props.lap.get_total_distance_km(), 2)}<span className="unit">km</span></div></td>);
      }
      else {
        columns.push(<td key='distance-miles' className="distance"><div className="cell-content">{xhale.helper.format_miles(this.props.lap.get_total_distance_miles(), 2)}<span className="unit">miles</span></div></td>);
      }
    }
    if (this.props.lap_features.duration) {
      columns.push(<td key='duration' className="duration"><div className="cell-content">{xhale.helper.format_time_seconds(this.props.lap.duration_seconds)}</div></td>);
    }
    if (this.props.lap_features.speed) {
      if (this.props.discipline == 'running') {
        if (this.props.distance_unit == 'km') {
          columns.push(<td key='speed-min-km' className="speed"><div className="cell-content">{xhale.helper.format_time(xhale.helper.meters_per_second_as_seconds_per_km(this.props.lap.get_speed_meters_per_second()))}<span className="unit">min/km</span></div></td>);
        }
        else {
          columns.push(<td key='speed-min-mile' className="speed"><div className="cell-content">{xhale.helper.format_time(xhale.helper.meters_per_second_as_seconds_per_mile(this.props.lap.get_speed_meters_per_second()))}<span className="unit">min/mi</span></div></td>);
        }
      }
      else {
        if (this.props.distance_unit == 'km') {
          columns.push(<td key='speed-kph' className="speed"><div className="cell-content">{xhale.helper.format_km(xhale.helper.meters_per_second_to_kph(this.props.lap.get_speed_meters_per_second()), 2)}<span className="unit">kph</span></div></td>);
        }
        else {
          columns.push(<td key='speed-mph' className="speed"><div className="cell-content">{xhale.helper.format_miles(xhale.helper.meters_per_second_to_mph(this.props.lap.get_speed_meters_per_second()), 2)}<span className="unit">mph</span></div></td>);
        }
      }
    }
    if (this.props.lap_features.height_climbed) {
      if (this.props.distance_unit == 'km') {
        columns.push(<td key='climb-meters' className="climb-meters"><div className="cell-content">{xhale.helper.decimal_places(this.props.lap.height_climbed_meters, 0, 'zero')}<span className="unit">m</span></div></td>);
      }
      else {
        columns.push(<td key='climb-feet' className="climb-feet"><div className="cell-content">{xhale.helper.decimal_places(xhale.helper.meters_to_feet(this.props.lap.height_climbed_meters))}<span className="unit">ft</span></div></td>);
      }
    }
    if (this.props.lap_features.heart_rate) {
      columns.push(<td key='heart-rate' className="heart-rate"><div className="cell-content">{xhale.helper.decimal_places(this.props.lap.average_heart_rate_bpm, 0)}<span className="unit">bpm</span></div></td>);
    }
    if (this.props.lap_features.average_power) {
      if (athlete_weight) {
        var average_watts_per_kg = xhale.helper.decimal_places(this.props.lap.average_power_watts / athlete_weight, 2, 'zero', true);
        var average_power_to_weight_jsx = (<span className="power-to-weight"> <span className="value">{average_watts_per_kg}</span><span className="unit">w/kg</span></span>);
      }
      else {
        var average_power_to_weight_jsx = '';
      }
      columns.push(<td key='average-power' className="average-power"><div className="cell-content">{xhale.helper.decimal_places(this.props.lap.average_power_watts, 0)}<span className="unit">watts</span>{average_power_to_weight_jsx}</div></td>);
    }
    if (this.props.lap_features.normalized_power) {
      if (athlete_weight) {
        var normalized_watts_per_kg = xhale.helper.decimal_places(this.props.lap.normalized_power_watts / athlete_weight, 2, 'zero', true);
        var normalized_power_to_weight_jsx = (<span className="power-to-weight"> <span className="value">{normalized_watts_per_kg}</span><span className="unit">w/kg</span></span>);
      }
      else {
        var normalized_power_to_weight_jsx = '';
      }
      columns.push(<td key='normalized-power' className="normalized-power"><div className="cell-content">{xhale.helper.decimal_places(this.props.lap.normalized_power_watts, 0)}<span className="unit">watts</span>{normalized_power_to_weight_jsx}</div></td>);
    }
    if (this.props.lap_features.cadence) {
      if (this.props.discipline == 'running') {
        columns.push(<td key='cadence' className="cadence"><div className="cell-content">{xhale.helper.decimal_places(this.props.lap.average_cadence_rpm * 2, 0)}<span className="unit">spm</span></div></td>);
      }
      else {
        columns.push(<td key='cadence' className="cadence"><div className="cell-content">{xhale.helper.decimal_places(this.props.lap.average_cadence_rpm, 0)}<span className="unit">rpm</span></div></td>);
      }
    }
    if (this.props.lap_features.has_lengths) {
      var cell_content = (this.props.lap.lengths.length > 0) ? (<div className="cell-content">&#65516;</div>) : '';
      columns.push(<td key='lengths-arrow' className="lengths-arrow">{cell_content}</td>);
    }

    var row_menu_items = [];
    var editing_this_lapset = this.props.lapset_edit_mode.active && this.props.lapset_edit_mode.editing_lapset == this.props.current_lapset.id && this.props.lapset_edit_mode.editing_activity == this.props.lap.activity_id;
    if (xhale.helper.has_beta_access('compare_sessions') && editing_this_lapset) {

      if (this.props.current_lapset.id != 'overall_activity') {
        if (this.props.lap.lapsets.indexOf(this.props.current_lapset.id) > -1) {
          row_menu_items.push(<span onClick={this.handleRemoveFromSetClicked} key='remove-from-set' className="remove-from-set table-button">remove from selection</span>);
        }
        else {
          row_menu_items.push(<span onClick={this.handleAddToSetClicked} key='remove-from-set' className="add-to-set table-button">add to selection</span>);
        }
      }

    }
    else {
      if (this.props.lap_features.hideable) {
        row_menu_items.push(<span key='hide' className="hide table-button" onClick={this.handleHideClicked}>hide</span>);
      }

      if (xhale.helper.has_beta_access('compare_sessions') && this.props.current_lapset.id != 'overall_activity') {
        row_menu_items.push(' ');
        row_menu_items.push(<span onClick={this.handleRemoveFromSetClicked} key='remove-from-set' className="remove-from-set table-button">remove from set</span>);
      }

      if (this.props.lap_features.comparable && xhale_meta_data.has_premium_features) {
        var button_text = xhale.helper.has_beta_access('compare_sessions') ? 'select' : 'compare';
        row_menu_items.push(' ');
        row_menu_items.push(<span onClick={this.handleCompareClicked} key='compare' className="compare table-button">{button_text}</span>);
      }
    }

    if (row_menu_items.length > 0) {
      columns.push(<td key="menu" className="menu"><div className="cell-content">{row_menu_items}</div></td>);
    }

    var visibility_class = (this.props.lap.visible) ? 'visible' : 'hidden';
    var highlightable_class = (this.props.highlightable) ? 'highlightable' : 'not-highlightable';
    var highlighted_class = (this.props.highlighted) ? 'highlighted' : 'not-highlighted';

    var text_colour = (this.props.lap.colour) ? this.props.lap.colour : '#333333';
    if (xhale.helper.has_beta_access('compare_sessions') && editing_this_lapset && this.props.lap.lapsets.indexOf(this.props.current_lapset.id) > -1) {
      text_colour = this.props.activity_colour;
    }

    return (
      <tr id={'lap-' + this.props.lap.id} data-lap-id={this.props.lap.id} onClick={this.handleRowClicked} className={visibility_class + ' ' + highlightable_class + ' ' + highlighted_class} style={{color: text_colour}}>
      {columns}
      </tr>
    );

  }
}

export var fetchLapSetActivitySummariesByTags = function (tags, activitiesFetchedCallback) {
  var data = {};
  data.athlete_id = athlete_data.id;
  data.tags = tags.join(',');
  $.getJSON('/training-logs/fetch-lapset-activity-summaries/', data).done(activitiesFetchedCallback);
}

export var fetchLapSetActivitySummariesBySessionIDs = function (session_ids, race_ids, activitiesFetchedCallback) {
  var data = {};
  data.athlete_id = athlete_data.id;
  data.session_ids = session_ids.join(',');
  data.race_ids = race_ids.join(',');
  $.getJSON('/training-logs/fetch-lapset-activity-summaries/', data).done(activitiesFetchedCallback);
}

export class CollectionRow extends React.Component {

  handleClick = () => {

    // Load the activities for this collection.
    fetchLapSetActivitySummariesBySessionIDs(this.props.collection.unsaved_session_ids, this.props.collection.unsaved_race_ids, this.props.activitiesFetchedCallback);

    // Record which collection has been opened.
    if (typeof(this.props.collectionLoadingCallback) == 'function') {

      // Automatically include the current training session in the collection.
      if (this.props.base_training_session_id) {
        this.props.collection.unsaved_session_ids = xhale.helper.add_to_array_if_not_exists(this.props.collection.unsaved_session_ids, this.props.base_training_session_id);
      }
      else if (this.props.base_race_id) {
        this.props.collection.unsaved_race_ids = xhale.helper.add_to_array_if_not_exists(this.props.collection.unsaved_race_ids, this.props.base_race_id);
      }

      this.props.collectionLoadingCallback(this.props.collection);
    }

  }

  render = () => {
    return <div className='collection' onClick={this.handleClick} style={{cursor: 'pointer', textDecoration: 'underline'}}>{this.props.collection.name}</div>
  }
}


export class OpenCollectionPopup extends React.Component {

  constructor(props) {
    
    super(props);

    var self = this;

    this.state = {
      existing_collection_id: null,
      existing_collections: [],
    }

    // Load existing collections.
    $.getJSON('/coach/get-session-collections/', {athlete_id: this.props.athlete_id}, function (data) {
      self.setState({existing_collections: data.session_collections});
    });

  }

  handleCancelClick = () => {
    this.props.closeOpenCollectionPopup();
  }

  render() {

    var container_css = {
      position: 'fixed',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 200
    }

    var popup_inner_css = {
      background: 'white',
      width: 500,
      margin: '60px auto 0',
      padding: '1px 15px 15px',
      top: 50,
    }

    var existing_sets;
    if (this.state.existing_sets.length > 0) {
      var lap_set_options = [];
      for (var i = 0; i < this.state.existing_sets.length; i++) {
        lap_set_options.push(<CollectionRow
          key={this.state.existing_sets[i].id}
          set={this.state.existing_sets[i]}
          activitiesFetchedCallback={this.props.activitiesFetchedCallback}
          setLoadingCallback={this.props.setLoadingCallback}
        />);
      }

    }

    return (
      <div id="lap-set-form-container" style={container_css}>
        <div className="lap-set-form-inner" style={popup_inner_css}>
          <a className="close" href="#" title="Close" onClick={this.handleCancelClick}>Close</a>
          <h2>Open a set</h2>
          {lap_set_options}
        </div>
      </div>
    );
  }
}

/**
 * Create a summary of the entire session.
 *
 * This normalises the comparison of the entire session as if it were a
 * set of laps. This saves us from having to explicitly create laps
 * covering an entire session while letting us use the same mechanics for
 * showing the charts.
 */
export var getActivitySummaries = function (activities) {

  var activity_summaries = [];
  for (var i = 0; i < activities.length; i++) {

    var activity = activities[i];

    var activity_summary = {}

    activity_summary.activity_id = activity.id;
    activity_summary.start_time = xhale.helper.new_zulu_datetime(activity.start_time);

    if (typeof(activity.training_session_id) != 'undefined') {
      activity_summary.training_session_id = activity.training_session_id;
    }
    if (typeof(activity.race_id) != 'undefined') {
      activity_summary.race_id = activity.race_id;
    }

    activity_summaries.push(activity_summary);
  }

  return activity_summaries;
};

/**
 * A React component showing a chart comparing activities.
 */
export class ComparisionChart extends React.Component {

  state = {
    tooltip: {
      visible: false,
    }
  }

  /**
   * Implement React's componentDidMount.
   */
  componentDidMount = () => {
    this.createChart();
    this.updateChart();
  }

  /**
   * Implement React's componentDidUpdate.
   */
  componentDidUpdate = () => {
    this.updateChart();
  }

  /**
   * Create the elements of the chart which do not change.
   */
  createChart = () => {

    var main_container = select(ReactDOM.findDOMNode(this));
    var chart_svg = main_container.select('svg');

    // Calculate chart dimension.
    this.margin = {top: 5, right: 10, bottom: 20, left: 90};
    this.width = chart_svg.node().getBoundingClientRect().width - this.margin.right - this.margin.left;

    // Define the scales.
    this.x_scale = scaleTime().range([0, this.width]).nice();

    // Define the axis.
    this.x_axis = axisBottom()
      .ticks(7)
      .tickFormat(function (d) { return d.format('d/m/Y') });

    // Construct the chart.
    this.drawing = {};

    this.drawing.chart_container = chart_svg.append("g")
      .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");

    this.drawing.x_axis = this.drawing.chart_container.append("g")
      .attr("class", "x axis")

    this.drawing.plot_area = this.drawing.chart_container.append("g")
      .attr("class", "plot-area")

    this.drawing.plot_area.append("path").attr("class", this.props.metric + "-data-area");

    this.drawing.y_axis = this.drawing.chart_container.append("g")
      .attr("class", "y axis")
      .attr("transform", "translate(0,0)");

    this.drawing.y_axis_label = this.drawing.chart_container.append("g")
    for (var i = 0; i < this.props.label_text_rows.length; i++) {
      this.drawing.y_axis_label.append("text")
        .attr("class", "y-axis-label y-axis-label-row-" + i + " " + this.props.metric)
        .style("font-size", "12px")
        .style("text-anchor", "start")
        .style("fill", "#ededee")
        .text(this.props.label_text_rows[i])
        .attr("dx", -85)
        .attr("dy", 10 + (i * 15));
    }

    this.drawing.plot_area.append("path").attr("class", this.props.metric + "-data-path");

  }

  getValueOfMetricFromLapSetActivitySummary = (lap_set_activity_summary, metric) => {
    if (metric == 'speed') {
      var meters_per_second = lap_set_activity_summary.distance_meters / lap_set_activity_summary.duration_seconds;
      if (this.props.discipline == 'running') {
        if (this.props.distance_unit == 'km') {
          return xhale.helper.meters_per_second_as_seconds_per_km(meters_per_second);
        }
        else if (this.props.distance_unit == 'miles') {
          return xhale.helper.meters_per_second_as_seconds_per_mile(meters_per_second);
        }
      }
      else {
        if (this.props.distance_unit == 'km') {
          return xhale.helper.meters_per_second_to_kph(meters_per_second);
        }
        else if (this.props.distance_unit == 'miles') {
          return xhale.helper.meters_per_second_to_mph(meters_per_second);
        }
      }
    }
    else if (metric == 'cadence') {
      return lap_set_activity_summary.average_cadence_rpm;
    }
    else if (metric == 'heart_rate') {
      return lap_set_activity_summary.average_heart_rate_bpm;
    }
    else if (metric == 'average_power') {
      return lap_set_activity_summary.average_power_watts;
    }

  }

  getStatusInSelection = (activity) => {

    if (activity.training_session_id) {
      var in_unsaved_selection = this.props.compare_sessions_current_selection.unsaved_session_ids.indexOf(activity.training_session_id) > -1;
      var in_saved_selection = this.props.compare_sessions_current_selection.saved_session_ids.indexOf(activity.training_session_id) > -1;
    }
    else if (activity.race_id) {
      var in_unsaved_selection = this.props.compare_sessions_current_selection.unsaved_race_ids.indexOf(activity.race_id) > -1;
      var in_saved_selection = this.props.compare_sessions_current_selection.saved_race_ids.indexOf(activity.race_id) > -1;
    }

    if (in_unsaved_selection && in_saved_selection) {
      return 'saved';
    }
    else if (in_unsaved_selection && !in_saved_selection) {
      return 'unsaved';
    }
    else {
      return 'removed';
    }

  }

  /**
   * Make changes to the chart that change when the data does.
   */
  updateChart = () => {

    var self = this;
    var chart_dimensions = this.refs.chart_container.getBoundingClientRect();

    this.height = 200;
    var ticks = 7;
    if (this.props.total_active_metrics > 1) {
      this.height = 180 / this.props.total_active_metrics;
      var ticks = ticks / this.props.total_active_metrics;
    }

    // Calculate chart values.
    var data = [];
    var dates = [];
    var lap_set = this.props.lapsets[this.props.current_lapset_id];

    this.drawing.x_axis.attr("transform", "translate(0," + this.height + ")");

    for (var i = 0; i < this.props.activity_summaries.length; i++) {

      var activity = this.props.activity_summaries[i];

      // If this activity has been removed from the selection, do not display it.
      if (this.getStatusInSelection(activity) == 'removed') {
        continue;
      }

      if (!lap_set) {
        continue;
      }

      var lap_set_activity_summary = this.props.lap_set_activity_summaries[activity.activity_id][this.props.current_lapset_id];

      if (!lap_set_activity_summary) {
        continue;
      }

      var datetime = activity.start_time;
      dates.push(datetime);

      var value = this.getValueOfMetricFromLapSetActivitySummary(lap_set_activity_summary, this.props.metric);

      if (activity.activity_id == this.props.base_activity_id) {
        var colour = get_discipline_colour(this.props.discipline);
      }
      else if (typeof(this.props.visible_lapset_activity_colours) != 'undefined' && typeof(this.props.visible_lapset_activity_colours[activity.activity_id]) != 'undefined') {
        var colour = this.props.visible_lapset_activity_colours[activity.activity_id];
      }
      else {
        var colour = '#c5c5c5';
      }

      if (value) {
        data.push({
          activity: activity,
          datetime: datetime,
          value: value,
          colour: colour
        });
      }

    }

    data.sort(function (a, b) { return a.datetime - b.datetime });

    // Display the chart in date order.
    var min_date = min(dates);
    var max_date = max(dates);

    this.x_scale.domain([min_date, max_date]);
    this.x_axis.scale(this.x_scale);
    this.drawing.x_axis.call(this.x_axis);

    // Calculate y scales based on updated data.
    if (self.props.discipline == 'running' && self.props.metric == 'speed') {
      var scale_lowest_value = max(data, function (d) {return d.value}) + 10; // Show at least an extra 10 seconds on top of the smallest value so all bars show.
      var scale_highest_value = min(data, function (d) {return d.value});
    }
    else {
      var scale_lowest_value = min(data, function (d) {return d.value});
      var scale_highest_value = max(data, function (d) {return d.value});
    }

    this.y_scale = scaleLinear()
      .domain([scale_highest_value, scale_lowest_value])
      .range([0, this.height]);

    // Construct a trend line.
    this.updateChartTrendLine(data, min_date, max_date);

    // Construct y axis.
    this.y_axis = axisLeft().ticks(ticks).scale(this.y_scale);

    if (self.props.discipline == 'running' && self.props.metric == 'speed') {
      this.y_axis.tickFormat(function (d) { return xhale.helper.format_time(d, false, false) });
    }
    else {
      this.y_axis.tickFormat(function (d) { return d });
    }

    // Redraw.
    this.drawing.y_axis.call(this.y_axis);

    var axis_data_line = line()
      .x((d) => { return this.x_scale(d.datetime); })
      .y((d) => { return this.y_scale(d.value); });

    var axis_data_area = area()
      .x((d) => { return this.x_scale(d.datetime); })
      .y0((d) => { return this.y_scale(d.value); })
      .y1(this.height);

    var axis_point = this.drawing.plot_area.selectAll("." + this.props.metric + "-point")
      .data(data);

    // Enter and update.
    axis_point.enter()
      .append("circle").attr("class", this.props.metric + "-point")
        .on("mouseover", this.handleMouseOverBar)
      .merge(axis_point)
        .attr("fill", function (d) {return d.colour; })
        .attr("cx", function (d) { return self.x_scale(d.datetime); })
        .attr("cy", function (d) { return self.y_scale(d.value); })
        .attr("r", function (d) { return 4})

    axis_point.exit().remove()

    var data_area = this.drawing.plot_area.selectAll("." + this.props.metric + "-data-area");
    var data_path = this.drawing.plot_area.selectAll("." + this.props.metric + "-data-path");

    data_area
      .datum(data)
      .attr("d", axis_data_area)
      .attr("stroke-width", '1')
      .attr("fill", '#5f5f5f')
      .attr("stroke", 'none')
      .attr("class", 'area ' + this.props.metric + '-data-area');

    data_path
      .datum(data)
      .attr("d", axis_data_line)
      .attr("stroke-width", '1')
      .attr("fill", 'none')
      .attr("stroke", '#eeeeee')
      .attr("class", 'line ' + this.props.metric + '-data-path');

    // Update the speed axis label to either miles or km.
    if (this.props.metric == 'speed') {
      this.drawing.y_axis_label.select('.y-axis-label-row-1').text(this.props.label_text_rows[1]);
    }

    if (this.props.discipline == 'running' && this.props.metric == 'speed' && typeof(target_run_paces) != 'undefined' && target_run_paces.length > 0) {

      var visible_target_run_paces = [];
      for (var i = 0; i < target_run_paces.length; i++) {
        if (target_run_paces[i].visible) {
          visible_target_run_paces.unshift(target_run_paces[i]);
        }
      }

      var pace_line_groups = this.drawing.plot_area.selectAll('.pace-line-group')
        .data(visible_target_run_paces, (d, i) => {
          // The key is unique per pace AND index so that a change in order
          // forces a redraw. This means the SVG elements always stack so that
          // lower pace lines appear last which helps to display the
          // 'pointer-background' element above other pace lines, making the
          // design a little clearer.
          return i + '-' + d.id;
        })

      var new_pace_line_groups = pace_line_groups.enter()
        .append("g")
        .attr("class", "pace-line-group")

      var get_y_scale_position = (d) => {
        if (this.props.distance_unit == 'km') {
          return this.y_scale(xhale.helper.meters_per_second_as_seconds_per_km(d.meters_per_second))
        }
        else {
          return this.y_scale(xhale.helper.meters_per_second_as_seconds_per_mile(d.meters_per_second))
        }
      }

      // The actual pace line.
      new_pace_line_groups.append("line")
        .attr("x1", 0)
        .attr("x2", chart_dimensions.width - 60)
        .attr("shape-rendering", 'crispEdges')
        .attr("stroke-dasharray", '2')
        .attr("stroke-width", '2')
        .attr("class", 'data-line pace-line');

      // Label.
      // ...a gray background surrounding the following line
      // to make it standout more clearly.
      new_pace_line_groups.append("line")
        .attr("y1", 10)
        .attr("stroke-width", '10')
        .attr("stroke", 'rgba(80, 80, 80, 0.8)')
        .attr("class", (d) => { return 'pace-label-pointer-background pace-id-' + d.id } )
        .attr("shape-rendering", 'crispEdges');

      // ...a line pointing to the label.
      new_pace_line_groups.append("line")
        .attr("y1", 10)
        .attr("fill", 'none')
        .attr("stroke-width", '2')
        .attr("stroke-dasharray", '2')
        .attr("shape-rendering", 'crispEdges')
        .attr("class", (d) => { return 'data-line pace-label-pointer pace-id-' + d.id } )

      // Speed label and label background.
      new_pace_line_groups.append("rect")
        .attr("y", 0)
        .attr("height", 22)
        .attr("fill", '#525252')
        .attr("class", (d) => { return 'pace-label-background pace-id-' + d.id } )
        .style("opacity", "0.8");

      new_pace_line_groups.append("text")
        .attr("class", (d) => { return 'pace-label pace-id-' + d.id } )
        .style("font-size", "11px")
        .style("font-weight", "normal")
        .style('fill', 'white')
        .style('text-anchor', 'start')
        .attr("dy", 15);

      pace_line_groups.exit().remove();

      pace_line_groups = pace_line_groups.merge(new_pace_line_groups);

      pace_line_groups.selectAll('.pace-line')
        .attr("y1", get_y_scale_position)
        .attr("y2", get_y_scale_position)

      pace_line_groups.selectAll('.pace-label-pointer-background')
        .attr("y2", (d) => { return get_y_scale_position(d) - 5 })

      pace_line_groups.selectAll('.pace-label-pointer')
        .attr("y2", get_y_scale_position)

      pace_line_groups.selectAll('.pace-label')
        .text((d) => {
          if (this.props.distance_unit == 'km') {
            return d.description + ' - ' + formatSecondsAsPace(xhale.helper.meters_per_second_as_seconds_per_km(d.meters_per_second)) + ' min/km';
          }
          else {
            return d.description + ' - ' + formatSecondsAsPace(xhale.helper.meters_per_second_as_seconds_per_mile(d.meters_per_second)) + ' min/mi';
          }
        })

      // Once drawn cycle through each visible pace line and position the labels
      // so that they don't overlap.
      var pointer_starting_x_point = 5;
      for (var i = 0; i < visible_target_run_paces.length; i++) {
        var pace_id = visible_target_run_paces[i].id;
        var text_element = select('.pace-label.pace-id-' + pace_id);
        var label_text_width = text_element.node().getComputedTextLength();

        select(".pace-label.pace-id-" + pace_id).attr("dx", pointer_starting_x_point + 5);
        select(".pace-label-background.pace-id-" + pace_id).attr("width", label_text_width + 6).attr("x", pointer_starting_x_point + 2);
        select(".pace-label-pointer-background.pace-id-" + pace_id).attr("x1", pointer_starting_x_point).attr("x2", pointer_starting_x_point);
        select(".pace-label-pointer.pace-id-" + pace_id).attr("x1", pointer_starting_x_point).attr("x2", pointer_starting_x_point);

        // Record how much further along we need to put the next label.
        pointer_starting_x_point += label_text_width + 20;
      }

    }

  }

  /**
   * Build and update the trend line on the chart.
   */
  updateChartTrendLine = (data, min_date, max_date) => {

    // Get the x and y values for least squares.
    var x_series = data.map((d) => { return this.x_scale(d.datetime)});
    var y_series = data.map((d) => { return d.value });

    // If there are fewer than 3 points it doesn't make sense to draw the line.
    // With 2 points it would just be a line in the same place as the actual
    // data line.
    if (y_series.length < 3) {
      return;
    }

    var least_squares = this.getLeastSquares(x_series, y_series);

    // Apply the results of the least squares regression.
    var x1 = this.x_scale(min_date);
    var x2 = this.x_scale(max_date);
    var y1 = this.y_scale(least_squares.slope + least_squares.intercept);
    var y2 = this.y_scale(least_squares.slope * x2 + least_squares.intercept);

    var trend_data = [{
      'x1': x1,
      'x2': x2,
      'y1': y1,
      'y2': y2,
    }];

    var trendline = this.drawing.plot_area.selectAll(".trendline")
      .data(trend_data);

    trendline.enter()
      .append("line")
        .attr("class", "trendline")
      .merge(trendline)
        .attr("x1", (d) => { return d.x1 })
        .attr("y1", (d) => { return d.y1 })
        .attr("x2", (d) => { return d.x2 })
        .attr("y2", (d) => { return d.y2 })
        .attr("stroke", "rgb(238, 238, 238)")
        .attr("stroke-dasharray", "6, 2");
  }

  /**
   * Given a series of x and y data return the slop, intercept and r-square
   * of the least squares best fit line.
   */
  getLeastSquares = (x_series, y_series) => {

    var reduceSumFunction = function(prev, cur) { return prev + cur; };

    var x_mean = x_series.reduce(reduceSumFunction) * 1.0 / x_series.length;
    var y_mean = y_series.reduce(reduceSumFunction) * 1.0 / y_series.length;

    var x_variance = x_series.map(function(d) { return Math.pow(d - x_mean, 2) }).reduce(reduceSumFunction);
    var y_variance = y_series.map(function(d) { return Math.pow(d - y_mean, 2) }).reduce(reduceSumFunction);
    var xy_variance = x_series.map(function(d, i) { return (d - x_mean) * (y_series[i] - y_mean) }).reduce(reduceSumFunction);

    var slope = xy_variance / x_variance;
    var intercept = y_mean - (x_mean * slope);
    var r_square = Math.pow(xy_variance, 2) / (x_variance * y_variance);

    return {
      slope: slope,
      intercept: intercept,
      r_square: r_square
    };
  }

  /**
   * Show a tooltip when the user hovers the mouse over a bar in the chart.
   */
  handleMouseOverBar = (d) => {

    // TODO display all the values in the tooltip, not just the metric we are hovering over.

    var chart_dimensions = this.refs.chart_container.getBoundingClientRect();

    var self = this;

    if (this.props.discipline == 'running' && this.props.metric == 'speed') {
      var value = xhale.helper.format_time(d.value, false, false);
    }
    else {
      var value = xhale.helper.decimal_places(d.value);
    }

    var tooltip = this.state.tooltip;
    tooltip.visible = true;
    tooltip.activity = d.activity;
    tooltip.value = this.props.metric + ': ' + value;
    tooltip.datetime = d.datetime;
    tooltip.link = "/session/" + d.activity.training_session_id + "/";
    tooltip.x_position = this.x_scale(d.datetime) - 25 + chart_dimensions.x;
    tooltip.y_position = this.y_scale(d.value) - 45;

    // Place the tooltip below if it would fall off the top of the page.
    if (tooltip.y_position <= 3) {
      tooltip.y_position += 102;
    }

    this.setState({tooltip: tooltip});

  }

  handleMouseLeave = (d) => {
    var tooltip = this.state.tooltip;
    tooltip.visible = false;
    this.setState({tooltip: tooltip});
  }

  toggleDisplayActivityOnChart = (event) => {
    this.props.toggleDisplayActivityOnChart(this.state.tooltip.activity.activity_id);
  }

  /**
   * Implement React's render.
   */
  render() {

    var tooltip;
    if (this.state.tooltip.visible) {
      tooltip = (
        <div className="tooltip" style={{
          width: '140px',
          height: '75px',
          padding: '5px',
          background: 'white',
          position: 'absolute',
          borderRadius: '5px',
          color: 'black',
          lineHeight: '19px',
          left: this.state.tooltip.x_position,
          top: this.state.tooltip.y_position,
          zIndex: 1,
        }}>
          <div>{this.state.tooltip.datetime.format('d/m/Y H:m')}</div>
          <div>{this.state.tooltip.value}</div>
          <div><a href={this.state.tooltip.link}>Open session</a></div>
          <div style={{cursor: 'pointer'}} onClick={this.toggleDisplayActivityOnChart}>show on chart</div>
        </div>
      );
    }

    var height = 206;
    if (this.props.total_active_metrics > 1) {
      height = height / this.props.total_active_metrics;
    }

    // The last chart should contain the axis, so provide room for it.
    if (this.props.position == this.props.total_active_metrics) {
      height = height + 25;
    }

    return (
      <div style={{height: height + 'px', width: '100%', clear: 'both'}} ref="chart_container" onMouseLeave={this.handleMouseLeave}>
        {tooltip}
        <svg style={{height: '100%', width: '100%'}} className="lap-sets-chart"></svg>
      </div>
    );
  }

}

var formatPaceTime = timeFormat("%M:%S"),
    formatSecondsAsPace = function(d) {
      d = Math.round(d);
      if (d > 3599) { // Don't try to format anything over an hour.
        return '--:--';
      }
      return formatPaceTime(new Date(2012, 0, 1, 0, 0, d));
    };

export const SessionCollection = function (saved_values) {
  this.id = saved_values.id;
  this.saved_session_ids = saved_values.session_ids.slice(0);
  this.saved_race_ids = saved_values.race_ids.slice(0);
  this.unsaved_session_ids = saved_values.session_ids.slice(0);
  this.unsaved_race_ids = saved_values.race_ids.slice(0);
  this.athlete_id = saved_values.athlete_id;
  this.name = (saved_values.name) ? saved_values.name : '';
}

SessionCollection.prototype.hasUnsavedChanges = function () {
  if (this.unsaved_session_ids.length != this.saved_session_ids.length) {
    return true;
  }
  else {
    this.saved_session_ids.sort();
    this.unsaved_session_ids.sort();
    var matches = this.saved_session_ids.every((session_id, index) => { return session_id === this.unsaved_session_ids[index]});
    return !matches;
  }
}

SessionCollection.prototype.save = function (sessionCollectionSavedCallback) {

  var session_ids_to_add = this.unsaved_session_ids.filter((i) => { return this.saved_session_ids.indexOf(i) == -1 });
  var session_ids_to_remove = this.saved_session_ids.filter((i) => { return this.unsaved_session_ids.indexOf(i) == -1 });

  var post_data = {
    existing_collection_id: this.id,
    name: this.name,
    athlete_id: this.athlete_id,
    session_ids_to_add: session_ids_to_add,
    session_ids_to_remove: session_ids_to_remove,
  }

  ajaxRequest({
    url: '/coach/save-session-collection/',
    method: 'POST',
    data: post_data,
    success: sessionCollectionSavedCallback,
    error: handleLoggedOutError,
    expected_response_type: 'json',
  });
}
